import { useLocalization } from '@lib/localization';
import { Box, Button, Dialog, FormLabel, IconButton, TextField, Typography } from '@mui/material';
import { type CustomFolderChildRow } from '@components/Projects/folderRows';
import {
  formDialogBodyStyles, formDialogCancelStyles, formDialogCloseStyles, formDialogFieldStyles,
  formDialogFooterStyles, formDialogHeaderStyles, formDialogLabelStyles, formDialogPaperStyles,
  formDialogSubmitStyles, formDialogTitleStyles
} from '@components/Shared/Dialog/formDialog.styles';
import { isFolder, useAddFolderMutation, useUpdateFolderMutation, type IFolder } from '@store/api/folderEndpoints';
import { XIcon } from 'lucide-react';
import React, { useId, useRef, useState } from 'react';
import FolderIcon from './FolderIcon';
import { folderIdOf, INVALID_FOLDER_NAME } from './folderTree';

interface FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The folder to rename; without it the modal adds a folder to `currentFolder`. */
  folderItem?: CustomFolderChildRow | null;
  currentFolder?: IFolder | null;
  /** Called once a rename has been saved, with the name it was given. */
  onRenamed?: (folderItem: CustomFolderChildRow, name: string) => void;
}

// Moving lives in MoveDialog; this modal only names a folder, new or existing.
const FolderModal: React.FC<FolderModalProps> = ({ isOpen, onClose, folderItem, currentFolder, onRenamed }) => {
  const [name, setName] = useState<string>('');
  const { t } = useLocalization();
  const titleId = useId();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [addFolder, { isLoading: isAddFolderLoading }] = useAddFolderMutation();
  const [updateFolder, { isLoading: isUpdateFolderLoading }] = useUpdateFolderMutation();
  const isLoading = isAddFolderLoading || isUpdateFolderLoading;

  const isRenameForm = Boolean(folderItem);

  // A name has to be unique among the folder's siblings only.
  const siblingNames = currentFolder?.children
    .filter(isFolder)
    .filter((child) => child.item._id !== folderItem?.id)
    .map((child) => child.item.name) ?? [];

  // Checked as it is typed, against the rules the API applies, so the answer is there before the
  // button is pressed.
  const trimmedName = name.trim();

  let nameError: string | null = null;
  if (INVALID_FOLDER_NAME.test(trimmedName)) nameError = t('Folder_New_InvalidChars');
  else if (siblingNames.includes(trimmedName)) nameError = t('Folder_New_NameTaken');

  // A new folder is offered a name of its own, as Drive's is, shown in the empty field and taken
  // when nothing is typed, so Enter alone makes one: the first free of "New folder", "New folder 2",
  // and so on.
  let defaultName = t('Folder_New');
  for (let number = 2; siblingNames.includes(defaultName); number++) defaultName = `${t('Folder_New')} ${number}`;
  const nameToSend = isRenameForm ? trimmedName : trimmedName || defaultName;

  // Nothing to send for an empty name, a refused one, or the name the folder already has.
  const canSubmit = Boolean(nameToSend) && !nameError && nameToSend !== folderItem?.name && !isLoading;

  // Set once as the dialog opens, so a listing read again meanwhile does not undo what was typed.
  const handleEnter = () => {
    if (folderItem) setName(folderItem.name);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    // A rename sends the folder's own parent back, which the backend reads as "not moved".
    const result = folderItem
      ? await updateFolder({
        _id: folderItem.id,
        name: nameToSend,
        parentFolder: folderIdOf(folderItem.item.parentFolder),
      })
      : await addFolder({ data: { name: nameToSend, parentFolder: currentFolder?._id ?? null } });

    if (result.error) return;

    if (folderItem) onRenamed?.(folderItem, nameToSend);
    handleClose();
  };

  const handleClose = () => {
    if (isLoading) return;

    setName('');
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby={titleId}
      slotProps={{
        paper: { sx: formDialogPaperStyles },
        // Focused once the dialog has opened rather than with `autoFocus`, which the dialog's focus
        // trap hands back to the button that opened it when React runs its effects twice (dev).
        transition: { onEnter: handleEnter, onEntered: () => inputRef.current?.focus() },
      }}
    >
      <form noValidate onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}>
        <Box sx={formDialogHeaderStyles}>
          {/* The folder as the page draws it, as a CRM heads a record with its kind's icon. */}
          <FolderIcon size={28} emblem={folderItem?.isSharedWithMe ? 'shared' : undefined} />
          <Typography id={titleId} component="h2" sx={formDialogTitleStyles}>
            {isRenameForm ? t('Common_Rename') : t('Folder_New')}
          </Typography>
          <IconButton aria-label={t('Common_Close')} onClick={handleClose} disabled={isLoading} sx={formDialogCloseStyles}>
            <XIcon size={18} />
          </IconButton>
        </Box>
        <Box sx={formDialogBodyStyles}>
          {/* Only a rename needs a name typed: a new folder left empty takes the one it is offered. */}
          <FormLabel htmlFor={inputId} required={isRenameForm} sx={formDialogLabelStyles}>
            {t('Common_Name')}
          </FormLabel>
          <TextField
            id={inputId}
            inputRef={inputRef}
            fullWidth
            size="small"
            required={isRenameForm}
            placeholder={isRenameForm ? t('Folder_New_Placeholder') : defaultName}
            value={name}
            onChange={(e) => setName(e.target.value)}
            // A renamed folder's name starts selected, so typing replaces it.
            onFocus={(e) => e.target.select()}
            error={Boolean(nameError)}
            // A line is kept for the error while there is none, so the footer does not move as it
            // comes and goes; the id ties it to the field.
            helperText={nameError ?? ' '}
            disabled={isLoading}
            slotProps={{ htmlInput: { maxLength: 255 } }}
            sx={formDialogFieldStyles}
          />
        </Box>
        <Box sx={formDialogFooterStyles}>
          <Button onClick={handleClose} disabled={isLoading} sx={formDialogCancelStyles}>
            {t('Common_Cancel')}
          </Button>
          <Button type="submit" disabled={!canSubmit} loading={isLoading} sx={formDialogSubmitStyles}>
            {isRenameForm ? t('Common_Save') : t('Common_Create')}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default FolderModal;
